import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getWatcher,
  createWatcher,
  updateWatcher,
  getAirportPresets,
  getProgramPresets,
  type Watcher,
  type MileageProgram,
} from '../lib/api';

type CabinClass = 'economy' | 'premium_economy' | 'business' | 'first';

const CABIN_OPTIONS: { value: CabinClass; label: string }[] = [
  { value: 'economy', label: 'Economy (Y)' },
  { value: 'premium_economy', label: 'Premium Economy (W)' },
  { value: 'business', label: 'Business (J)' },
  { value: 'first', label: 'First (F)' },
];

const DEFAULT_WATCHER: Omit<Watcher, 'id' | 'createdAt' | 'updatedAt'> = {
  name: '',
  enabled: true,
  origins: [],
  destinations: [],
  programs: [],
  excludeAirlines: [],
  cabinClasses: ['business', 'first'],
  dateStart: '',
  dateEnd: '',
  directOnly: false,
  maxPoints: {},
  maxPriceUsd: {},
  minSeats: 1,
  centsPerPoint: {},
  notifications: [{ type: 'email', emails: [] }],
};

export function WatcherEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const [formData, setFormData] = useState<Omit<Watcher, 'id' | 'createdAt' | 'updatedAt'>>(DEFAULT_WATCHER);
  const [airportPresets, setAirportPresets] = useState<Record<string, string[]>>({});
  const [airportPresetGroups, setAirportPresetGroups] = useState<Record<string, Record<string, string[]>>>({});
  const [programs, setPrograms] = useState<MileageProgram[]>([]);
  const [defaultCpp, setDefaultCpp] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Temp inputs for adding airports
  const [originInput, setOriginInput] = useState('');
  const [destInput, setDestInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [excludeAirlineInput, setExcludeAirlineInput] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [presetsData, programData] = await Promise.all([
          getAirportPresets(),
          getProgramPresets(),
        ]);
        setAirportPresets(presetsData.presets);
        setAirportPresetGroups(presetsData.groups);
        setPrograms(programData.programs);
        setDefaultCpp(programData.defaultCpp);

        if (id) {
          const watcher = await getWatcher(id);
          const { id: _, createdAt: __, updatedAt: ___, ...rest } = watcher;
          setFormData(rest);
        } else {
          // Set default CPP values for new watcher
          setFormData(prev => ({
            ...prev,
            centsPerPoint: { ...programData.defaultCpp },
          }));
        }
      } catch (err) {
        setError('Failed to load data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isNew) {
        await createWatcher(formData);
      } else {
        await updateWatcher(id!, formData);
      }
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addAirport = (field: 'origins' | 'destinations', value: string) => {
    // Split by comma and trim, but preserve case for preset names like "Japan"
    const entries = value.split(',').map(s => s.trim()).filter(Boolean);
    const existing = formData[field];
    const newEntries = entries.filter(e => !existing.includes(e));
    if (newEntries.length > 0) {
      setFormData({ ...formData, [field]: [...existing, ...newEntries] });
    }
  };

  const removeAirport = (field: 'origins' | 'destinations', code: string) => {
    setFormData({ ...formData, [field]: formData[field].filter(c => c !== code) });
  };

  const addPreset = (field: 'origins' | 'destinations', presetName: string) => {
    // Add preset name directly - backend will expand to airport codes
    const existing = formData[field];
    if (!existing.includes(presetName)) {
      setFormData({ ...formData, [field]: [...existing, presetName] });
    }
  };

  const toggleProgram = (program: string) => {
    const current = formData.programs;
    if (current.includes(program)) {
      setFormData({ ...formData, programs: current.filter(p => p !== program) });
    } else {
      setFormData({ ...formData, programs: [...current, program] });
    }
  };

  const toggleCabin = (cabin: CabinClass) => {
    const current = formData.cabinClasses;
    if (current.includes(cabin)) {
      setFormData({ ...formData, cabinClasses: current.filter(c => c !== cabin) });
    } else {
      setFormData({ ...formData, cabinClasses: [...current, cabin] });
    }
  };

  const updateCpp = (program: string, value: number) => {
    setFormData({
      ...formData,
      centsPerPoint: { ...formData.centsPerPoint, [program]: value },
    });
  };

  const updateMaxPoints = (cabin: CabinClass, value: number | undefined) => {
    const updated = { ...formData.maxPoints };
    if (value === undefined) {
      delete updated[cabin];
    } else {
      updated[cabin] = value;
    }
    setFormData({ ...formData, maxPoints: updated });
  };

  const updateMaxPrice = (cabin: CabinClass, value: number | undefined) => {
    const updated = { ...formData.maxPriceUsd };
    if (value === undefined) {
      delete updated[cabin];
    } else {
      updated[cabin] = value;
    }
    setFormData({ ...formData, maxPriceUsd: updated });
  };

  const addEmail = (email: string) => {
    if (!email.trim() || !email.includes('@')) return;
    const notification = formData.notifications[0];
    if (notification?.type === 'email') {
      const emails = notification.emails || [];
      if (!emails.includes(email)) {
        setFormData({
          ...formData,
          notifications: [{ ...notification, emails: [...emails, email.trim()] }],
        });
      }
    }
  };

  const removeEmail = (email: string) => {
    const notification = formData.notifications[0];
    if (notification?.type === 'email') {
      setFormData({
        ...formData,
        notifications: [{
          ...notification,
          emails: (notification.emails || []).filter(e => e !== email),
        }],
      });
    }
  };

  const addExcludeAirline = (value: string) => {
    // Split by comma, uppercase, and trim
    const codes = value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    const existing = formData.excludeAirlines;
    const newCodes = codes.filter(c => !existing.includes(c));
    if (newCodes.length > 0) {
      setFormData({ ...formData, excludeAirlines: [...existing, ...newCodes] });
    }
  };

  const removeExcludeAirline = (code: string) => {
    setFormData({ ...formData, excludeAirlines: formData.excludeAirlines.filter(c => c !== code) });
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  const emails = formData.notifications[0]?.type === 'email'
    ? formData.notifications[0].emails || []
    : [];

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-8">
        {isNew ? 'Create Watcher' : 'Edit Watcher'}
      </h2>

      {error && <div className="bg-rose-50 text-rose-800 border border-rose-200 rounded-lg p-4 mb-4 text-sm">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Basic Info */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Basic Info</h3>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Name</label>
            <input
              type="text"
              className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Asia to US Business"
              required
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="w-4 h-4 accent-slate-600"
              checked={formData.enabled}
              onChange={e => setFormData({ ...formData, enabled: e.target.checked })}
            />
            <label>Enabled</label>
          </div>
        </div>

        {/* Route */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Route</h3>

          {/* Origins */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Origins</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="flex-1 block px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={originInput}
                onChange={e => setOriginInput(e.target.value)}
                placeholder="Codes or regions (e.g., LAX, Japan)"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAirport('origins', originInput);
                    setOriginInput('');
                  }
                }}
              />
              <button
                type="button"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none"
                onClick={() => {
                  addAirport('origins', originInput);
                  setOriginInput('');
                }}
              >
                Add
              </button>
            </div>
            <div className="mb-2">
              {Object.entries(airportPresetGroups).map(([groupName, presets]) => (
                <div key={groupName} className="mb-2">
                  <div className="text-xs text-gray-500 mb-1 font-medium">
                    {groupName}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(presets).map(preset => (
                      <button
                        key={preset}
                        type="button"
                        className={`text-xs px-2 py-1 rounded cursor-pointer border-none ${formData.origins.includes(preset) ? 'bg-blue-300' : 'bg-gray-200 hover:bg-gray-300'}`}
                        onClick={() => addPreset('origins', preset)}
                        title={presets[preset]?.join(', ')}
                      >
                        + {preset}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.origins.map(code => (
                <span
                  key={code}
                  className="inline-flex items-center gap-2 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 cursor-pointer"
                  onClick={() => removeAirport('origins', code)}
                >
                  <strong>{code}</strong>
                  {Array.isArray(airportPresets[code]) && (
                    <span className="opacity-70">
                      {airportPresets[code].join(', ')}
                    </span>
                  )}
                  <span>&times;</span>
                </span>
              ))}
            </div>
          </div>

          {/* Destinations */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Destinations</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="flex-1 block px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={destInput}
                onChange={e => setDestInput(e.target.value)}
                placeholder="Codes or regions (e.g., NRT, Japan)"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAirport('destinations', destInput);
                    setDestInput('');
                  }
                }}
              />
              <button
                type="button"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none"
                onClick={() => {
                  addAirport('destinations', destInput);
                  setDestInput('');
                }}
              >
                Add
              </button>
            </div>
            <div className="mb-2">
              {Object.entries(airportPresetGroups).map(([groupName, presets]) => (
                <div key={groupName} className="mb-2">
                  <div className="text-xs text-gray-500 mb-1 font-medium">
                    {groupName}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(presets).map(preset => (
                      <button
                        key={preset}
                        type="button"
                        className={`text-xs px-2 py-1 rounded cursor-pointer border-none ${formData.destinations.includes(preset) ? 'bg-green-300' : 'bg-gray-200 hover:bg-gray-300'}`}
                        onClick={() => addPreset('destinations', preset)}
                        title={presets[preset]?.join(', ')}
                      >
                        + {preset}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.destinations.map(code => (
                <span
                  key={code}
                  className="inline-flex items-center gap-2 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 cursor-pointer"
                  onClick={() => removeAirport('destinations', code)}
                >
                  <strong>{code}</strong>
                  {Array.isArray(airportPresets[code]) && (
                    <span className="opacity-70">
                      {airportPresets[code].join(', ')}
                    </span>
                  )}
                  <span>&times;</span>
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              className="w-4 h-4 accent-slate-600"
              checked={formData.directOnly}
              onChange={e => setFormData({ ...formData, directOnly: e.target.checked })}
            />
            <label>Direct flights only</label>
          </div>

          {/* Exclude Airlines */}
          <div className="mt-4">
            <label className="block mb-2 text-sm font-medium">Exclude Airlines (Carriers)</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                className="flex-1 block px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={excludeAirlineInput}
                onChange={e => setExcludeAirlineInput(e.target.value)}
                placeholder="e.g., AC, NZ, ET"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addExcludeAirline(excludeAirlineInput);
                    setExcludeAirlineInput('');
                  }
                }}
              />
              <button
                type="button"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none"
                onClick={() => {
                  addExcludeAirline(excludeAirlineInput);
                  setExcludeAirlineInput('');
                }}
              >
                Add
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Exclude flights operated by these airlines. Uses IATA codes (e.g., AC for Air Canada).
            </p>
            {formData.excludeAirlines.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.excludeAirlines.map(code => (
                  <span
                    key={code}
                    className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-rose-100 text-rose-700 cursor-pointer"
                    onClick={() => removeExcludeAirline(code)}
                  >
                    {code} &times;
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Date Range */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Date Range</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium">Start Date</label>
              <input
                type="date"
                className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={formData.dateStart}
                onChange={e => setFormData({ ...formData, dateStart: e.target.value })}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium">End Date</label>
              <input
                type="date"
                className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={formData.dateEnd}
                onChange={e => setFormData({ ...formData, dateEnd: e.target.value })}
                required
              />
            </div>
          </div>
        </div>

        {/* Programs & CPP */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Mileage Programs</h3>
          <p className="text-xs text-gray-500 mb-4">
            Select programs to monitor. Leave empty to monitor all. Set CPP (cents per point) for each program.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {programs.map(program => (
              <div
                key={program.code}
                className={`border border-gray-200 rounded-lg p-3 ${formData.programs.includes(program.code) ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-slate-600"
                    checked={formData.programs.includes(program.code)}
                    onChange={() => toggleProgram(program.code)}
                  />
                  <label className="font-medium">{program.name}</label>
                </div>
                <div className="flex items-center gap-2 ml-6">
                  <span className="text-xs text-gray-500">CPP:</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-20 px-2 py-1 text-sm border border-gray-200 rounded bg-white focus:outline-none focus:border-slate-500"
                    value={formData.centsPerPoint[program.code] ?? defaultCpp[program.code] ?? ''}
                    onChange={e => updateCpp(program.code, parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cabins & Price Limits */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Cabin Classes & Price Limits</h3>

          <div className="space-y-3 md:space-y-4">
            {CABIN_OPTIONS.map(({ value, label }) => (
              <div
                key={value}
                className={`border border-gray-200 rounded-lg p-3 md:p-4 ${formData.cabinClasses.includes(value) ? 'bg-green-50' : ''}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-slate-600"
                    checked={formData.cabinClasses.includes(value)}
                    onChange={() => toggleCabin(value)}
                  />
                  <label className="font-medium text-sm md:text-base">{label}</label>
                </div>

                {formData.cabinClasses.includes(value) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 ml-0 sm:ml-6">
                    <div>
                      <label className="text-xs text-gray-500">Max Points</label>
                      <input
                        type="number"
                        className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                        placeholder="e.g., 135000"
                        value={formData.maxPoints[value] ?? ''}
                        onChange={e => updateMaxPoints(value, e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Max Price (USD)</label>
                      <input
                        type="number"
                        className="block w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                        placeholder="e.g., 2500"
                        value={formData.maxPriceUsd[value] ?? ''}
                        onChange={e => updateMaxPrice(value, e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4">
            <label className="block mb-2 text-sm font-medium">Minimum Available Seats</label>
            <input
              type="number"
              min="1"
              className="w-24 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
              value={formData.minSeats}
              onChange={e => setFormData({ ...formData, minSeats: parseInt(e.target.value) || 1 })}
            />
            <p className="mt-1 text-xs text-gray-500">For family travel, set higher to find flights with multiple seats.</p>
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
          <h3 className="font-semibold mb-4">Notifications</h3>

          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 md:p-4 mb-4 text-xs md:text-sm text-sky-700">
            <strong>Notification Logic:</strong>
            <ul className="list-disc ml-4 md:ml-5 mt-2 space-y-1">
              <li>First discovery of a matching flight: notify</li>
              <li>Price drops below last notified price: notify again</li>
              <li>Price stays same or increases: skip (no spam)</li>
            </ul>
            <p className="mt-2 opacity-80">
              Each date + route + cabin + program combination is tracked separately.
            </p>
          </div>

          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">Email Addresses</label>
            <div className="flex gap-2 mb-2">
              <input
                type="email"
                className="flex-1 block px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-500/20"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                placeholder="email@example.com"
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEmail(emailInput);
                    setEmailInput('');
                  }
                }}
              />
              <button
                type="button"
                className="inline-flex items-center justify-center px-3 md:px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none"
                onClick={() => {
                  addEmail(emailInput);
                  setEmailInput('');
                }}
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1">
              {emails.map(email => (
                <span
                  key={email}
                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 cursor-pointer break-all"
                  onClick={() => removeEmail(email)}
                >
                  {email} &times;
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 sm:justify-end">
          <button
            type="button"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none"
            onClick={() => navigate('/')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-800 transition-colors cursor-pointer border-none disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Saving...' : isNew ? 'Create Watcher' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
